import Button, { ButtonTypes } from '@/components/Buttons/Button';
import {
  Divider,
  Flex,
  Grid,
  Group,
  Stack,
  Text,
  createStyles,
} from '@mantine/core';
import { ITechstack, ITechstackDetailTechblog } from '@/types/techstack';
import { addRefParam, isDark } from '@/utils/utils';

import { Badge } from '@/components/Badge';
import DMText from '@/components/DMText';
import Link from 'next/link';
import themeVariables from '@/styles/themeVariables';

const useTechstackDetailTechblogStyle = createStyles((theme) => ({
  techblogCardWrapper: {
    cursor: 'pointer',
    transition: '300ms all ease',
    ':hover': {
      transform: 'translateX(3px)',
    },
  },

  techblogCardTitle: {
    fontSize: 22,
    fontWeight: 400,
    [theme.fn.smallerThan('sm')]: {
      fontSize: 16,
      fontWeight: 500,
    },
  },

  techblogCardSummary: {
    fontSize: 15,
    fontWeight: 400,
    [theme.fn.smallerThan('sm')]: {
      fontSize: 14,
    },
  },

  divider: {
    margin: '18px 0px',
    [theme.fn.smallerThan('sm')]: {
      margin: '6px 0px',
    },
  },
}));

const TechstackDetailTechblog = ({ data }: { data: ITechstack }) => {
  const { dark } = isDark();
  const { classes } = useTechstackDetailTechblogStyle();

  const TechblogCard = ({
    techblog,
  }: {
    techblog: ITechstackDetailTechblog;
  }) => (
    <Stack
      className={classes.techblogCardWrapper}
      onClick={() => window.open(addRefParam(techblog.link), '_blank')}
    >
      <Text
        size={13}
        weight={600}
        color={
          dark ? themeVariables.colors.blue200 : themeVariables.colors.blue300
        }
      >
        {techblog.company.name}
      </Text>
      <DMText
        className={classes.techblogCardTitle}
        lineClamp={1}
        dColor={themeVariables.darkMode.normal}
      >
        {techblog.title}
      </DMText>
      <DMText
        className={classes.techblogCardSummary}
        lineClamp={2}
        dColor={themeVariables.darkMode.alternative}
      >
        {techblog.summary}
      </DMText>
      <Group spacing={6} h={20} sx={{ overflow: 'hidden' }}>
        {techblog.tags.length > 0 &&
          techblog.tags.map((techstack) => (
            <Badge key={techstack} text={techstack} />
          ))}
      </Group>
      <Divider
        className={classes.divider}
        size={1}
        color={themeVariables.line.neutral}
      />
    </Stack>
  );

  return data?.techblog_list && data?.techblog_list?.length > 0 ? (
    <>
      <Flex justify={'space-between'} mt={80}>
        <DMText
          size={22}
          weight={600}
          lts={-0.4}
          dColor={themeVariables.darkMode.normal}
        >
          기술 블로그 글
        </DMText>
        <Link href={`/techblog/list?tag=${data?.techstack?.id}`}>
          <Button
            type={ButtonTypes.transparentGray}
            style={{ color: themeVariables.darkMode.alternative(dark) }}
          >
            더 보기
          </Button>
        </Link>
      </Flex>
      <Grid columns={24} gutter={20} mt={24}>
        {data?.techblog_list.map((item) => (
          <Grid.Col key={item.id} md={12}>
            <TechblogCard techblog={item} />
          </Grid.Col>
        ))}
      </Grid>
    </>
  ) : null;
};

export default TechstackDetailTechblog;
